/**
 * Route table for the corporate site.
 *
 * Phase 2 ships the shell, so most entries render a titled placeholder. They
 * exist as real routes from day one because a mega-menu that links to 404s is
 * worse than one with fewer links, and `nav.test.tsx` asserts that every href
 * in the navigation resolves to something here.
 */

import {
  ALL_PRODUCTS,
  INDUSTRIES,
  SOLUTIONS,
  WARDEN_SERVICES,
} from "./nav/navigation";
import { BIFROST_PAGES } from "./content/bifrost-site";
import { LEGAL_PAGES } from "./content/bifrost-legal";
import { SENTIENCE_PAGES } from "./content/sentience-site";
import { GUARDIAN_PAGES } from "./content/guardian-site";

export type RouteDef = {
  path: string;
  title: string;
  eyebrow?: string;
  lede?: string;
};

const PRODUCT_ROUTES: RouteDef[] = ALL_PRODUCTS.map((p) => ({
  path: p.href,
  title: p.name,
  eyebrow: p.descriptor,
  lede: p.blurb,
}));

const SOLUTION_ROUTES: RouteDef[] = SOLUTIONS.map((s) => ({
  path: s.href,
  title: s.name,
  eyebrow: "Solution",
  lede: s.blurb,
}));

const INDUSTRY_ROUTES: RouteDef[] = INDUSTRIES.map((i) => ({
  path: i.href,
  title: i.name,
  eyebrow: "Industry",
  lede: i.blurb,
}));

const WARDEN_ROUTES: RouteDef[] = WARDEN_SERVICES.flatMap((group) =>
  group.links.map((link) => ({
    path: link.href,
    title: link.label,
    eyebrow: `Warden · ${group.title}`,
  })),
);

const PILLAR_ROUTES: RouteDef[] = [
  {
    path: "/products",
    title: "Products",
    eyebrow: "Portfolio",
    lede: "Cybersecurity for the agentic era and continuous, verifiable compliance."
  },
  {
    path: "/ai-security",
    title: "AI Security",
    eyebrow: "Pillar",
    lede: "Cerberus, Argus, Mimir and Guardian: defend agents at runtime, test them adversarially, detect poisoned memory, and keep delegated authority human-controlled.",
  },
  {
    path: "/governance",
    title: "Governance & Compliance",
    eyebrow: "Pillar",
    lede: "TraceLock, Sentience and Bifrost: prove your controls continuously, execute regulatory change, and answer the buyer's security review with signed evidence.",
  },
];

const CORE_ROUTES: RouteDef[] = [
  {
    path: "/solutions",
    title: "Solutions",
    eyebrow: "By outcome",
    lede: "Start from the problem you have to solve, not the product you have to buy.",
  },
  {
    path: "/warden",
    title: "Warden",
    eyebrow: "Governed Autonomy · By Odingard",
    lede: "The engineers behind Cerberus, Argus and TraceLock, applied to your agent estate.",
  },
  {
    path: "/industries",
    title: "Industries",
    eyebrow: "By sector",
    lede: "Governed AI adoption in the industries where getting it wrong is a regulatory event.",
  },
  { path: "/resources", title: "Resource Library", eyebrow: "Resources" },
  {
    path: "/research",
    title: "Research",
    eyebrow: "Resources",
    lede: "Preprints on AI agent security: transitive taint propagation, containment survivability, machine-unlearning verification, runtime circuit breakers and calibrated confidence.",
  },
  { path: "/blog", title: "Blog", eyebrow: "Resources" },
  { path: "/customers", title: "Case Studies", eyebrow: "Resources" },
  { path: "/glossary", title: "Glossary", eyebrow: "Resources" },
  { path: "/docs", title: "Technical Documentation", eyebrow: "Resources" },
  {
    path: "/demos",
    title: "Demos and Trials",
    eyebrow: "Get started",
    lede: "Evaluate every Odingard product on your terms: a live demo, a self-serve workspace, open source, or a scoped pilot.",
  },
  {
    path: "/company",
    title: "Company",
    eyebrow: "Odingard",
    lede: "Odingard Security builds the controls and the evidence that let enterprises put autonomous AI to work without losing control of it.",
  },
  { path: "/contact", title: "Contact Us", eyebrow: "Company" },
  { path: "/search", title: "Search", eyebrow: "Odingard" },
  { path: "/privacy", title: "Privacy Notice", eyebrow: "Legal" },
  { path: "/terms", title: "Terms of Service", eyebrow: "Legal" },
  {
    path: "/security-disclosure",
    title: "Responsible Disclosure",
    eyebrow: "Legal",
  },
  { path: "/sitemap", title: "Sitemap", eyebrow: "Odingard" },
];

const BIFROST_ROUTES: RouteDef[] = BIFROST_PAGES.filter(
  (p) => p.key !== "overview",
).map((p) => ({
  path: p.path,
  title: p.title,
  eyebrow: "Bifrost",
  lede: p.lede,
}));

const SENTIENCE_ROUTES: RouteDef[] = SENTIENCE_PAGES.filter(
  (p) => p.key !== "overview",
).map((p) => ({
  path: p.path,
  title: p.title,
  eyebrow: "Sentience",
  lede: p.lede,
}));

const GUARDIAN_ROUTES: RouteDef[] = GUARDIAN_PAGES.filter(
  (p) => p.key !== "overview",
).map((p) => ({
  path: p.path,
  title: p.title,
  eyebrow: "Guardian",
  lede: p.lede,
}));

export const ROUTES: RouteDef[] = [
  ...PILLAR_ROUTES,
  ...PRODUCT_ROUTES,
  ...BIFROST_ROUTES,
  ...LEGAL_PAGES.map((p) => ({
    path: p.path,
    title: p.title,
    eyebrow: "Bifrost",
    lede: p.lede,
  })),
  ...SENTIENCE_ROUTES,
  ...GUARDIAN_ROUTES,
  ...SOLUTION_ROUTES,
  ...WARDEN_ROUTES,
  ...INDUSTRY_ROUTES,
  ...CORE_ROUTES,
];
